import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Copy, MessageCircle, Printer, Edit, Copy as Duplicate, ArrowRightCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR, formatDate } from '../lib/format';
import { waLink } from '../lib/whatsapp';

const STATUS_LABELS = { draft: 'Draft', sent: 'Sent', viewed: 'Viewed', accepted: 'Accepted', expired: 'Expired', lost: 'Lost' };

export default function QuotationView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [settings, setSettings] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saveScope, setSaveScope] = useState('one_time'); // 'record' | 'one_time'

  const load = () => Promise.all([
    api.get(`/quotations/${id}`).then(r => setQ(r.data)),
    api.get('/settings').then(r => setSettings(r.data)),
    api.get('/reason-codes').then(r => setReasons(r.data)),
    api.get('/quotations').then(r => setSiblings(r.data)),
  ]);
  useEffect(() => { load(); }, [id]);
  if (!q || !settings) return <div className="p-6 text-sm text-[var(--muted)]">Loading…</div>;

  const parent = q.parent_id || q.id;
  const versions = siblings.filter(s => (s.parent_id || s.id) === parent).sort((a,b) => a.version - b.version);

  const shareUrl = `${window.location.origin}/public/quotation/${id}`;
  const covering = `Hi ${q.client_name},\n\nYour quotation ${q.number} is ready.\nTotal ${formatINR(q.total)} · Advance ${formatINR(q.advance_amount || Math.round(q.total*(q.advance_pct||30)/100))}\n\nView: ${shareUrl}\n\nWarmly,\nRishika`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); };
  const copyMsg = () => { navigator.clipboard.writeText(covering); toast.success('Message copied'); };
  const waSend = () => window.open(waLink('', covering), '_blank');

  const duplicate = async () => {
    const r = await api.post(`/quotations/${id}/duplicate`);
    toast.success(`v${r.data.version} created`);
    nav(`/quotations/${r.data.id}/edit`);
  };
  const setCurrent = async () => { await api.post(`/quotations/${id}/set-current`); toast.success('Set current'); load(); };
  const convert = async () => {
    if (!window.confirm('Convert this quotation to a confirmed project + invoice?')) return;
    const r = await api.post(`/quotations/${id}/convert-to-booking`, {});
    toast.success(`Booking created · ${r.data.invoice_number}`);
    nav(`/invoices/${r.data.invoice_id}`);
  };

  const saveEdit = async (partial) => {
    if (saveScope === 'record') {
      await api.put(`/quotations/${id}`, { ...q, ...partial });
      toast.success('Saved to record');
    } else {
      setQ({ ...q, ...partial });
      toast.success('One-time edit (not saved)');
    }
  };

  const advance = q.advance_amount || Math.round((q.total || 0) * (q.advance_pct || 30) / 100);

  return (
    <div className="px-4 py-5 lg:px-10 lg:py-8 max-w-3xl mx-auto pb-32">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap print:hidden">
        <Link to="/quotations" className="eyebrow text-[var(--gold)]">← Quotations</Link>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setEditMode(!editMode)} className={editMode?'btn-gold':'btn-ghost'} data-testid="qv-edit-toggle"><Edit size={14}/>{editMode?' Preview':' Inline edit'}</button>
          <Link to={`/quotations/${id}/edit`} className="btn-ghost">Full edit</Link>
          <button onClick={duplicate} className="btn-ghost" data-testid="qv-duplicate"><Duplicate size={14}/> New version</button>
          {q.status !== 'accepted' && <button onClick={convert} className="btn-gold" data-testid="qv-convert"><ArrowRightCircle size={14}/> Convert to Booking</button>}
        </div>
      </div>

      {versions.length > 1 && (
        <div className="card-cream p-3 mb-4 flex gap-2 flex-wrap items-center print:hidden">
          <div className="eyebrow">Versions</div>
          {versions.map(v => (
            <Link key={v.id} to={`/quotations/${v.id}`}
              className="px-2 py-1 text-xs rounded"
              style={{background: v.id===id?'var(--charcoal)':'transparent', color: v.id===id?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>
              v{v.version} · {STATUS_LABELS[v.status]}{v.is_current?' ✓':''}
            </Link>
          ))}
          {!q.is_current && <button onClick={setCurrent} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">Set as current</button>}
        </div>
      )}

      {editMode && (
        <div className="card-alt p-3 mb-3 flex items-center justify-between print:hidden">
          <div className="text-xs">Inline edits will be saved:</div>
          <div className="flex gap-2">
            <button onClick={() => setSaveScope('one_time')} className="px-3 py-1 text-xs rounded" style={{background: saveScope==='one_time'?'var(--charcoal)':'transparent', color: saveScope==='one_time'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>One-time (this send)</button>
            <button onClick={() => setSaveScope('record')} className="px-3 py-1 text-xs rounded" style={{background: saveScope==='record'?'var(--charcoal)':'transparent', color: saveScope==='record'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>Save to record</button>
          </div>
        </div>
      )}

      <QuotationDocument q={q} settings={settings} reasons={reasons} advance={advance} editMode={editMode} onSave={saveEdit}/>

      {/* Fixed action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 print:hidden" style={{background:'var(--cream)', borderTop:'1px solid var(--line)', padding:'12px max(16px, env(safe-area-inset-left)) max(12px, env(safe-area-inset-bottom))'}}>
        <div className="max-w-3xl mx-auto flex gap-2 flex-wrap justify-center">
          <button onClick={() => window.print()} className="btn-ghost text-xs" data-testid="qv-print"><Printer size={12}/> Save PDF</button>
          <button onClick={waSend} className="btn-gold text-xs" data-testid="qv-wa"><MessageCircle size={12}/> Share on WhatsApp</button>
          <button onClick={copyLink} className="btn-ghost text-xs" data-testid="qv-link"><Copy size={12}/> Copy link</button>
          <button onClick={copyMsg} className="btn-ghost text-xs" data-testid="qv-msg"><Copy size={12}/> Copy message</button>
        </div>
      </div>
    </div>
  );
}

export function QuotationDocument({ q, settings, reasons = [], advance, editMode, onSave }) {
  const brandShort = settings.brand_name.split('—')[0].trim();
  const docText = settings.document_text || {};
  const reasonLabel = (code) => (reasons.find(r => r.code === code)?.label) || code;
  const groups = q.event_groups || [];
  const Ed = ({ v, k, cls = '', tag = 'span' }) => {
    if (!editMode) return React.createElement(tag, { className: cls }, v || '');
    return React.createElement(tag, {
      className: cls, contentEditable: true, suppressContentEditableWarning: true,
      onBlur: (e) => { const t = e.currentTarget.innerText; if (t !== v) onSave({ [k]: t }); },
      style: { outline: '1px dashed var(--gold)', outlineOffset: 2, padding: '0 2px' }
    }, v || '');
  };

  return (
    <div className="doc card-cream overflow-hidden" data-testid="quotation-doc">
      <div className="inv-head flex justify-between gap-6" style={{display:'flex'}}>
        <div>
          <div className="inv-brand-name">{brandShort}</div>
          <div className="inv-brand-tag">{settings.tagline}</div>
          <div className="inv-brand-meta">
            {settings.location_line}<br/>{settings.phone} &nbsp;·&nbsp; {settings.instagram}<br/>{settings.email}
          </div>
        </div>
        <div className="inv-doc-type text-right">
          <h1>Quotation</h1>
          <div className="inv-num">{q.number} · v{q.version}<br/>Issued {formatDate(q.created_at)}{q.expires_on?` · Valid until ${formatDate(q.expires_on)}`:''}</div>
        </div>
      </div>

      <div className="inv-status">
        <span>{q.status.toUpperCase()}</span>
        <span>{q.is_current ? 'Current version' : 'Superseded'}</span>
      </div>

      <div className="inv-body">
        <div className="inv-cols">
          <div>
            <div className="label">Prepared For</div>
            <p className="name"><Ed v={q.client_name} k="client_name"/></p>
          </div>
          <div>
            <div className="label">Events</div>
            {groups.map(g => (
              <div key={g.id} className="mb-1">
                <p><b>{g.name}</b> — {formatDate(g.date)}</p>
                {g.reporting_time && <p className="sub">Reporting {g.reporting_time}{g.ready_by_time?` · Ready ${g.ready_by_time}`:''}</p>}
                {g.venue && <p className="sub">{g.venue}</p>}
              </div>
            ))}
          </div>
        </div>

        {groups.map(g => <EventGroupRender key={g.id} group={g} reasonLabel={reasonLabel}/>)}

        {q.complimentary && (
          <div className="inv-includes" style={{background:'transparent', borderLeft:'2px solid var(--gold)'}}>
            <div className="label" style={{color:'var(--gold)'}}>Complimentary Upgrade</div>
            <div className="text-sm mt-1">{q.complimentary}</div>
          </div>
        )}

        <div className="inv-pay">
          <div className="inv-paybox">
            <div className="row"><span>List value</span><span className="mono-num">{formatINR(q.subtotal + (q.travel_lines||[]).reduce((s,l) => s + Number(l.amount||0), 0))}</span></div>
            {q.package_discount > 0 && <div className="row" style={{color:'var(--gold)'}}><span>{docText.quotation_savings_label || 'You save'}</span><span className="mono-num">− {formatINR(q.package_discount)}</span></div>}
            <div className="row total"><span>Package total</span><span className="mono-num">{formatINR(q.total)}</span></div>
            <div className="row adv"><span>Advance ({q.advance_pct}%) — locks the date(s)</span><span className="mono-num">{formatINR(advance)}</span></div>
            {(q.value_given_away||0) > 0 && (
              <div className="row" style={{borderTop:'1px solid var(--line)', marginTop:8, paddingTop:8, color:'var(--muted)', fontSize:12}}>
                <span>{docText.value_given_line || 'Total value included at no charge'}</span>
                <span className="mono-num">{formatINR(q.value_given_away)}</span>
              </div>
            )}
          </div>
        </div>

        {q.notes && <div className="text-sm text-[var(--muted)] italic mt-4"><Ed v={q.notes} k="notes"/></div>}

        {(q.terms||[]).length > 0 && (
          <div className="inv-terms">
            <div className="label">Terms &amp; Conditions</div>
            <div className="inv-terms-grid">
              {q.terms.map((t, i) => (
                <div className="term" key={i}><b>{t.title}</b>{(t.body||'').replace('{advance_pct}', q.advance_pct).replace('{validity_days}', settings.quotation_validity_days || 10)}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="inv-foot">
        <div className="n">{settings.brand_name}</div>
        {settings.location_line} &nbsp;·&nbsp; {settings.phone} &nbsp;·&nbsp; {settings.instagram} &nbsp;·&nbsp; {settings.website}
      </div>
    </div>
  );
}

function EventGroupRender({ group, reasonLabel }) {
  const eventSub = (group.services||[]).reduce((s, l) => {
    if (l.now != null) return s + l.now;
    if (l.pricing_mode === 'complimentary') return s;
    const base = (l.qty||0)*(l.rate||0);
    if (l.pricing_mode === 'flat') return s + Math.max(base - Number(l.discount_value||0), 0);
    if (l.pricing_mode === 'pct') return s + Math.max(base - base * Number(l.discount_value||0)/100, 0);
    if (l.pricing_mode === 'per_head') return s + Math.max(base - (l.qty||0)*Number(l.discount_value||0), 0);
    return s + base;
  }, 0);
  const eventTravel = (group.travel_lines||[]).reduce((s, l) => s + Number(l.amount||0), 0);
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between border-b hairline pb-1 mb-2">
        <div><span className="serif text-[17px]">{group.name}</span> <span className="text-xs text-[var(--muted)] tracking-[0.14em] uppercase ml-2">{formatDate(group.date)}</span></div>
      </div>
      <table className="inv-table" style={{borderTop:0}}>
        <tbody>
          {(group.services||[]).map((s, i) => {
            const base = (s.qty||0)*(s.rate||0);
            let now = base, saving = 0;
            if (s.pricing_mode === 'flat') { saving = Math.min(Number(s.discount_value||0), base); now = Math.max(base - saving, 0); }
            else if (s.pricing_mode === 'pct') { saving = Math.round(base * Number(s.discount_value||0)/100); now = Math.max(base - saving, 0); }
            else if (s.pricing_mode === 'per_head') { saving = (s.qty||0)*Number(s.discount_value||0); now = Math.max(base - saving, 0); }
            else if (s.pricing_mode === 'complimentary') { saving = base; now = 0; }
            const isComp = s.pricing_mode === 'complimentary';
            return (
              <tr key={i}>
                <td>
                  <div className="svc">{s.name}</div>
                  {s.sub && <div className="svc-sub">{s.sub}</div>}
                  {isComp && s.reason_code && <div className="svc-sub" style={{color:'var(--gold)'}}>Complimentary — {reasonLabel(s.reason_code)}</div>}
                </td>
                <td className="r">{s.qty}</td>
                <td className="r">
                  {saving > 0 && <span className="was">{new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(base)}</span>}
                  <span className="now">{new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(now)}</span>
                </td>
              </tr>
            );
          })}
          {(group.travel_lines||[]).map((t, i) => (
            <tr key={'t'+i}><td><div className="svc-sub">Travel · {t.label}</div></td><td className="r"></td><td className="r"><span className="now">{new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(t.amount)}</span></td></tr>
          ))}
        </tbody>
      </table>
      <div className="text-right text-xs text-[var(--muted)] pt-2">Event subtotal <span className="mono-num" style={{color:'var(--charcoal)', marginLeft:8}}>{new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(eventSub + eventTravel)}</span></div>
    </div>
  );
}
